import { useState } from 'react'
import {
  ShoppingBag,
  CalendarPlus,
  Sparkles,
  LifeBuoy,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { Tooltip } from '@/shared/ui/tooltip'

/**
 * ⛔ DO NOT TOUCH — SETTLED DESIGN (user directive, 2026-08-02).
 *
 * The dock's design, placement and overlap behaviour are final. That includes
 * it sitting over page content: the 2026-08 design pass proposed reserving
 * space for it and reworking its hit area, and both were rejected. Don't
 * re-open it, and don't change other screens' layout to work around it. Ask
 * first. See the matching banner on `.action-dock` in `index.css`.
 *
 * Bottom-centre rapid-action dock (spec/clubmanager/15 BR-17). The single bottom
 * action surface — it replaced the topbar ＋Venta and absorbed the
 * topbar Feedback button and the bottom-right Novedades FAB, so the
 * topbar slims to status + identity and there's no floating FAB.
 *
 * Two groups, divided: **create** (Venta · Reserva) │ **utility**
 * (Novedades · Feedback). Novedades only appears when something is
 * pending (count badge); when collapsed, an unread accent dot rides the
 * peek handle so the signal survives.
 *
 * **Peek-handle collapse.** Collapsing slides the solid panel down out
 * of view, leaving only a small low-opacity chevron tab — faint at
 * rest, solid on hover/focus/press, ALWAYS solid on touch (`(hover:
 * none)` — the iPad desk has no hover). State persisted per device.
 *
 * **Icon-only** — the desk reads the glyphs instantly; Tooltip +
 * aria-label keep them discoverable (topbar-icon pattern).
 *
 * Pure `shared/` chrome — owns *no* feature logic. The composition root
 * (app-shell) injects every trigger + the unread count via props
 * (dependency inversion → the eslint `boundaries` rule is satisfied
 * with no shared→feature exception).
 *
 * Mounted once in app-shell, gated to non-preview staff there.
 */

const DOCK_KEY = 'cm.action-dock'

export interface ActionDockProps {
  /**
   * Opens the shared counter-sale panel (app-shell wires the store). `null`
   * hides the "Nueva venta" button — used when the POS módulo is off
   * (`docs/PLAN-clubmanager-pwa.md` M2 progressive disclosure).
   */
  onQuickSale: (() => void) | null
  /** Starts a new booking (app-shell navigates to Hoy's CTA). */
  onNewBooking: () => void
  /** Opens the Centro de novedades panel (app-shell wires the store). */
  onNovedades: () => void
  /** Opens the feedback dialog (app-shell owns its open-state). */
  onFeedback: () => void
  /** Pending Novedades items; 0 ⇒ no Novedades affordance / no dot. */
  novedadesUnread: number
}

export function ActionDock({
  onQuickSale,
  onNewBooking,
  onNovedades,
  onFeedback,
  novedadesUnread,
}: ActionDockProps) {
  const [expanded, setExpanded] = useState<boolean>(
    () => localStorage.getItem(DOCK_KEY) !== 'collapsed',
  )

  const setOpen = (next: boolean) => {
    setExpanded(next)
    localStorage.setItem(DOCK_KEY, next ? 'expanded' : 'collapsed')
  }

  const tab = expanded ? 0 : -1
  const hasUnread = novedadesUnread > 0

  return (
    <div
      className="action-dock"
      data-collapsed={!expanded}
      role="region"
      aria-label="Acciones rápidas"
    >
      <div className="action-dock-panel" aria-hidden={!expanded}>
        <Tooltip content="Nueva reserva">
          <button
            type="button"
            className="action-dock-btn"
            aria-label="Nueva reserva"
            tabIndex={tab}
            onClick={onNewBooking}
          >
            <CalendarPlus size={18} aria-hidden="true" />
          </button>
        </Tooltip>
        {onQuickSale && (
          <Tooltip content="Nueva venta">
            <button
              type="button"
              className="action-dock-btn"
              aria-label="Nueva venta"
              tabIndex={tab}
              onClick={onQuickSale}
            >
              <ShoppingBag size={18} aria-hidden="true" />
            </button>
          </Tooltip>
        )}

        <span className="action-dock-div" aria-hidden="true" />

        {hasUnread && (
          <Tooltip content="Novedades">
            <button
              type="button"
              className="action-dock-btn action-dock-btn--has-badge"
              aria-label={`Novedades · ${novedadesUnread} pendiente${novedadesUnread === 1 ? '' : 's'}`}
              tabIndex={tab}
              onClick={onNovedades}
            >
              <Sparkles size={18} aria-hidden="true" />
              <span className="action-dock-badge mono" aria-hidden="true">
                {novedadesUnread}
              </span>
            </button>
          </Tooltip>
        )}
        <Tooltip content="Enviar feedback">
          <button
            type="button"
            className="action-dock-btn action-dock-btn--util"
            aria-label="Enviar feedback"
            tabIndex={tab}
            onClick={onFeedback}
          >
            <LifeBuoy size={18} aria-hidden="true" />
          </button>
        </Tooltip>

        <button
          type="button"
          className="action-dock-collapse"
          aria-label="Ocultar acciones rápidas"
          tabIndex={tab}
          onClick={() => setOpen(false)}
        >
          <ChevronDown size={15} aria-hidden="true" />
        </button>
      </div>

      <Tooltip content="Acciones rápidas">
        <button
          type="button"
          className="action-dock-handle"
          data-unread={hasUnread || undefined}
          aria-label={
            hasUnread
              ? `Mostrar acciones rápidas · ${novedadesUnread} novedad${novedadesUnread === 1 ? '' : 'es'}`
              : 'Mostrar acciones rápidas'
          }
          aria-expanded={expanded}
          tabIndex={expanded ? -1 : 0}
          onClick={() => setOpen(true)}
        >
          <span className="action-dock-grip" aria-hidden="true" />
          <ChevronUp size={15} aria-hidden="true" />
        </button>
      </Tooltip>
    </div>
  )
}
